import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HttpClient } from '../client/http';
import { SdkError } from '../client/errors';

/**
 * Estados possíveis da subscription do user no app.
 * Mapeamento do backend (uppercase Asaas-style) pro lowercase do template:
 *
 *   TRIAL (trialEndsAt > now)          → 'trialing'
 *   TRIAL (trialEndsAt <= now)         → 'expired'
 *   ACTIVE                             → 'active'
 *   PAST_DUE                           → 'past_due'
 *   CANCELLED / REVOKED                → 'canceled'
 *   PENDING / PENDING_AUTHORIZATION    → 'pending'
 *   EXPIRED                            → 'expired'
 *   (sem row)                          → 'none'
 */
export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'expired'
  | 'canceled'
  | 'past_due'
  | 'pending'
  | 'none';

export type CheckoutCycle = 'MONTHLY' | 'YEARLY';
export type CheckoutMethod = 'card' | 'pix-auto' | 'pix-once';

/**
 * Dados crus do cartão coletados inline pelo app (PCI SAQ-D — Asaas widget JS
 * não existe; backend tokeniza via /v3/creditCard/tokenize). PAN + CVV são
 * enviados via HTTPS direto pro backend. Nada persistido no SDK.
 */
export interface CheckoutCardData {
  number: string;
  holderName: string;
  expiryMonth: string;
  expiryYear: string;
  ccv: string;
}

/**
 * Holder info exigido pela API Asaas (KYC + antifraude). `phone` é opcional
 * tanto no SDK quanto no backend.
 */
export interface CheckoutHolderInfo {
  name: string;
  email: string;
  cpfCnpj: string;
  postalCode: string;
  addressNumber: string;
  phone?: string;
}

export interface CardCheckoutArgs {
  method: 'card';
  cycle: CheckoutCycle;
  cpf: string;
  card: CheckoutCardData;
  holderInfo: CheckoutHolderInfo;
  /** IP do cliente (opcional — backend usa header X-Forwarded-For como fallback). */
  remoteIp?: string;
}

export interface PixAutoCheckoutArgs {
  method: 'pix-auto';
  cycle: CheckoutCycle;
  cpf: string;
}

export interface PixOnceCheckoutArgs {
  method: 'pix-once';
  cycle: CheckoutCycle;
  cpf: string;
}

/**
 * Discriminated union pra `subscription.checkout(args)`. TS narrow por
 * `args.method` — `card` exige `card`+`holderInfo`; pix-auto e pix-once
 * só `cpf`+`cycle`.
 */
export type CheckoutArgs = CardCheckoutArgs | PixAutoCheckoutArgs | PixOnceCheckoutArgs;

export interface CardCheckoutResult {
  method: 'card';
  subscriptionId: string;
  /** `null` quando widget path não passou last4 OU backend não conseguiu inferir. */
  cardLast4: string | null;
  /** `null` em mesmas condições de `cardLast4`. */
  cardBrand: string | null;
  trialEndsAt: string | null;
  valueCents: number;
  cycle: CheckoutCycle;
}

export interface PixAutoCheckoutResult {
  method: 'pix-auto';
  subscriptionId: string;
  pixAuthorizationId: string;
  externalAuthorizationId: string;
  qrCodePayload: string | null;
  qrCodeBase64: string | null;
  valueCents: number;
  cycle: CheckoutCycle;
}

export interface PixOnceCheckoutResult {
  method: 'pix-once';
  subscriptionId: string;
  externalPaymentId: string;
  qrCodePayload: string | null;
  qrCodeBase64: string | null;
  expiresAt: string | null;
  valueCents: number;
  cycle: CheckoutCycle;
}

/**
 * Discriminated union do retorno de `subscription.checkout`. Caller narrow
 * por `result.method` (success) ou pelo discriminador `ok: false` (failure).
 *
 * **BREAKING (TS-only) vs SDK 0.13.x:** o tipo antigo `CheckoutResult` tinha
 * shape flat com `invoiceUrl: string` (hosted checkout). PR-A do backend
 * removeu o hosted checkout — o tipo flat foi substituído por esta union.
 *
 * **0.16.0 addition:** `MethodUnavailableResult` (W1B-7). Surfaces per-app
 * feature gates (e.g. `payments.checkout.pix_once_disabled` 403) without
 * forcing the caller to wrap every checkout in try/catch.
 */
export type CheckoutResult =
  | CardCheckoutResult
  | PixAutoCheckoutResult
  | PixOnceCheckoutResult
  | MethodUnavailableResult;

/**
 * @deprecated Use `subscription.checkout({ method: 'card', ... })`. Mantido
 * por 1 minor (remoção planejada em 0.15.0).
 */
export interface CheckoutCardArgs {
  cycle?: CheckoutCycle;
  cpf: string;
  card: CheckoutCardData;
  holderInfo: CheckoutHolderInfo;
  remoteIp?: string;
}

/**
 * @deprecated Use `subscription.checkout({ method: 'pix-auto' | 'pix-once', ... })`.
 */
export interface CheckoutPixArgs {
  cycle?: CheckoutCycle;
  cpf: string;
}

/**
 * @deprecated Use {@link CardCheckoutResult} via `subscription.checkout({ method: 'card', ... })`.
 */
export type CheckoutPixAutoResult = PixAutoCheckoutResult;

/**
 * @deprecated Use {@link PixOnceCheckoutResult} via `subscription.checkout({ method: 'pix-once', ... })`.
 */
export type CheckoutPixOnceResult = PixOnceCheckoutResult;

/**
 * Discriminated failure variant returned by `subscription.checkout(args)` when
 * a per-app feature gate refuses the requested method (e.g. `pix-once` is
 * disabled via `app_subscriptions_config.allow_pix_once`). Backend returns
 * 403 with code `payments.checkout.pix_once_disabled`; SDK catches it here
 * instead of throwing so callers (template's `usePaywallState`) can hide the
 * tab + fall back to another method without a try/catch around every call.
 */
export interface MethodUnavailableResult {
  ok: false;
  code: 'method_unavailable';
  method: CheckoutMethod;
  /** Backend error code as-shipped, e.g. `payments.checkout.pix_once_disabled`. */
  reason: string;
}

/**
 * Reactive state of which checkout methods the runtime has confirmed are
 * available for this app. Starts permissive (`null`) and flips to `false`
 * once a 403 is observed; never flips back to `true` automatically (a fresh
 * mount or `refresh()` is required to recheck).
 */
export interface MethodAvailability {
  card: boolean | null;
  'pix-auto': boolean | null;
  'pix-once': boolean | null;
}

/**
 * PIX pending state exposed reactively while a PIX checkout is awaiting
 * confirmation. Shape matches backend's flat keys (`qrCodePayload`,
 * `qrCodeBase64`) — NOT nested under `payload` (W1 finding 2026-04-25).
 */
export interface PixPending {
  qrCodePayload: string;
  qrCodeBase64: string | null;
  /** Which checkout method seeded this pending state. */
  method: 'pix-auto' | 'pix-once';
}

/**
 * Resultado de `subscription.cancel()`. Backend retorna os campos `refunded*`
 * quando F33 (janela de garantia 14d) aplica — SDK surface tanto pra UI
 * conseguir informar estorno quanto pra compliance CDC.
 */
export interface CancelResult {
  subscriptionId: string;
  /** Status já mapeado pro lowercase do template. */
  subscriptionStatus: 'canceled';
  /** ID do payment estornado no Asaas (F33 dentro da janela 14d). */
  refundedPaymentId?: string;
  /** Valor estornado em centavos (mesmo valor da lastTx CONFIRMED). */
  refundedValueCents?: number;
}

/**
 * Full response of `GET /api/app/:appUuid/payments/subscription/me`.
 * Backend returns the row plus an authoritative `hasAccess` boolean so the
 * SDK doesn't have to recompute the gate from `status` + dates locally.
 *
 * Shape established by W1B-3 (W1 backend contract verification, 2026-04-25):
 * the spec previously assumed user + subscription on a single `/me`
 * endpoint, but the real backend splits user data (auth/me) from
 * subscription data (this endpoint).
 */
export interface SubscriptionMeResponse {
  subscription: SubscriptionData | null;
  hasAccess: boolean;
}

/**
 * Payload completo da assinatura do user (1 row em `user_subscriptions`).
 * Shape espelha `GET /api/app/:appId/payments/subscription/me` — ver
 * `backend/src/payments/routes/selfService.ts`. Status fica em UPPERCASE
 * (direto do backend); quem quiser o lowercase mapeado usa `status()`.
 * Todos os timestamps são ISO 8601.
 */
export interface SubscriptionData {
  id: string;
  status:
    | 'TRIAL'
    | 'ACTIVE'
    | 'PAST_DUE'
    | 'CANCELLED'
    | 'REVOKED'
    | 'PENDING'
    | 'PENDING_AUTHORIZATION'
    | 'EXPIRED'
    | (string & {});
  paymentMethod: 'credit_card' | 'pix_automatico' | null;
  cycle: 'MONTHLY' | 'YEARLY' | null;
  valueCents: number | null;
  trialEndsAt: string | null;
  graceEndsAt: string | null;
  currentPeriodEnd: string | null;
  refundWindowEndsAt: string | null;
  canceledAt: string | null;
  createdAt: string;
}

export interface SubscriptionState {
  status: () => SubscriptionStatus;
  daysLeftInTrial: () => number | null;
  /**
   * Reactive snapshot of the most recent subscription row (null when user
   * has no row — modo free, pré-checkout, or logged-out). Updated by
   * `refresh()` and by background polling after a PIX checkout.
   *
   * **W1B-3 rename (SDK 0.16.0):** previously named `data` — renamed to
   * disambiguate from the new async `data()` method (a one-shot fetch).
   * Apps reading the reactive snapshot should migrate `subscription.data`
   * → `subscription.current`. Apps wanting a fresh fetch use `await
   * subscription.data()`.
   */
  current: SubscriptionData | null;
  /**
   * Reactive snapshot of the backend's `hasAccess` flag from the most
   * recent `/payments/subscription/me`. `null` until first refresh.
   * Authoritative gate value — prefer this over recomputing from
   * `current?.status` locally.
   */
  hasAccess: boolean | null;
  /**
   * Reactive map of which checkout methods the runtime has confirmed are
   * available for this app. Starts `null` for each (unknown) and flips to
   * `false` when the backend returns a per-app gate 403 (W1B-7). UI uses
   * this to hide unavailable tabs without a separate config check.
   */
  methodAvailability: MethodAvailability;
  /**
   * Reactive PIX pending state — non-null while a PIX checkout (auto or
   * once) is awaiting confirmation. Apps render a QR code from
   * `qrCodePayload` (BR Code text) or `qrCodeBase64` (PNG). Cleared when
   * polling settles, `clearPixPending()` is called from the consumer
   * (e.g. user dismissed the QR modal), or `subscription.cancel()` is
   * called. Shape matches backend's flat keys (W1 finding: NOT nested
   * under `payload`).
   */
  pixPending: PixPending | null;
  /**
   * Clears any in-flight `pixPending` state from the consumer side.
   * Wired through template's `dismissPix()` so user-dismissed PIX QR
   * modals mark themselves resolved without waiting for backend polling
   * to settle. Background polling continues — this is a UI affordance,
   * not a checkout cancellation. Use `cancel()` to actually stop a
   * pending subscription.
   *
   * **Added in SDK 0.26.0.** Resolves G154 (PIX dismiss no-op + memo
   * identity → modal reopen loop). Previously: template's `dismissPix`
   * was `() => {}` and apps had to maintain a local `pixActive` flag.
   */
  clearPixPending: () => void;
  /**
   * Fresh one-shot fetch of `GET /api/app/:appUuid/payments/subscription/me`.
   * Bypasses local cache; returns backend's authoritative row + hasAccess.
   * Useful for imperative checks (e.g. before a critical action) where the
   * reactive `current`/`hasAccess` snapshot may be stale.
   *
   * **W1B-3 (SDK 0.16.0):** new method, replaces the old `data` field
   * shape. Hits `/api/app/:appUuid/payments/subscription/me` (NOT `/me`,
   * which returns user-only data — W1 finding 2026-04-25).
   */
  data: () => Promise<SubscriptionMeResponse>;
  refresh: () => Promise<void>;
  loading: boolean;
  /**
   * G68: flipa `true` uma vez por (appId, userId) depois do primeiro
   * `/payments/subscription/me` resolver (sucesso ou falha). Enquanto `false`
   * + `sub === null` (cache miss), `SubscriptionGate` renderiza nada em vez
   * de mostrar paywall — evita flash de 2s em cada cold start pra user
   * já pago. Flipa só 1× por lifecycle → seguro como dep de useMemo.
   */
  initialLoadComplete: boolean;
  /**
   * API unificada de checkout. Discriminated por `args.method`:
   *
   * - `method: 'card'` — tokeniza cartão no backend (Asaas `/v3/creditCard/tokenize`)
   *   e cria subscription com `nextDueDate = today + trialDays`. Nenhuma cobrança
   *   day-0. Trial roda enquanto Asaas já segura o instrumento.
   * - `method: 'pix-auto'` — Pix Automático BACEN (Jornada 3). Retorna QR pra
   *   user autorizar débito recorrente no app do banco. Captura simbólica R$0,01.
   * - `method: 'pix-once'` — Pix avulso (1 ciclo, sem recorrência). **Gated por
   *   `app_subscriptions_config.allow_pix_once`** — backend retorna 403 quando
   *   desabilitado. Fluxo legacy pra apps com opt-in explícito.
   */
  checkout: (args: CheckoutArgs) => Promise<CheckoutResult>;
  /**
   * @deprecated Use `subscription.checkout({ method: 'card', ... })`. Removal
   * planned in 0.15.0.
   */
  checkoutCard: (args: CheckoutCardArgs) => Promise<CardCheckoutResult>;
  /**
   * @deprecated Use `subscription.checkout({ method: 'pix-auto', ... })`.
   * Removal planned in 0.15.0.
   */
  checkoutPixAuto: (args: CheckoutPixArgs) => Promise<PixAutoCheckoutResult>;
  /**
   * @deprecated Use `subscription.checkout({ method: 'pix-once', ... })`.
   * Removal planned in 0.15.0.
   */
  checkoutPixOnce: (args: CheckoutPixArgs) => Promise<PixOnceCheckoutResult>;
  /**
   * Cancela assinatura corrente. Retorna `CancelResult` com info de refund
   * (quando F33 janela 14d aplica). Caller deve chamar `refresh()` depois pra
   * propagar novo status pros consumers reactivos.
   */
  cancel: () => Promise<CancelResult>;
}

function mapStatus(sub: SubscriptionData | null): SubscriptionStatus {
  if (!sub) return 'none';
  const now = Date.now();
  if (sub.status === 'TRIAL') {
    if (sub.trialEndsAt && new Date(sub.trialEndsAt).getTime() <= now) return 'expired';
    return 'trialing';
  }
  if (sub.status === 'ACTIVE') return 'active';
  if (sub.status === 'PAST_DUE') return 'past_due';
  if (sub.status === 'CANCELLED' || sub.status === 'REVOKED') return 'canceled';
  if (sub.status === 'PENDING' || sub.status === 'PENDING_AUTHORIZATION' || sub.status === 'PENDING_CAPTURE') return 'pending';
  if (sub.status === 'EXPIRED') return 'expired';
  console.warn('sdk.subscription.unknown_status', { status: sub.status });
  return 'none';
}

export { mapStatus as _mapStatus };

function daysLeft(sub: SubscriptionData | null): number | null {
  if (!sub?.trialEndsAt) return null;
  const ms = new Date(sub.trialEndsAt).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

// G68: cache local do último status conhecido, pra hidratar `sub` otimista-
// mente em cold start. Sem isso, toda abertura do app renderiza paywall
// (sub inicial = null → mapStatus = 'none' → BLOCKING) até o /me resolver
// (~1-2s), piscando a tela de pagamento pra user já pago.
// Escopo por (appId, userId) pra não vazar estado entre users no mesmo device.
// TTL curto não é crítico: /me roda em toda montagem e corrige; cache só
// cobre o gap entre render inicial e primeira response.
const CACHE_PREFIX = 'hook:sub:';

function cacheKey(appId: string, userId: string): string {
  return `${CACHE_PREFIX}${appId}:${userId}`;
}

function readCache(appId: string, userId: string): SubscriptionData | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(appId, userId));
    if (!raw) return null;
    return JSON.parse(raw) as SubscriptionData;
  } catch {
    return null;
  }
}

function writeCache(
  appId: string,
  userId: string,
  sub: SubscriptionData | null,
): void {
  if (typeof window === 'undefined') return;
  try {
    const key = cacheKey(appId, userId);
    if (sub === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(sub));
  } catch {
    // quota/disabled — silent fail; /me em próximo mount repovoa o estado
  }
}

// Background polling tunables for PIX confirmation. 3s × 60 = 180s total —
// enough for typical user-scans-then-pays flow. Caller can extend by calling
// checkout again after expiry; SDK never auto-restarts.
const PIX_POLL_INTERVAL_MS = 3000;
const PIX_POLL_MAX_ATTEMPTS = 60;

const INITIAL_METHOD_AVAILABILITY: MethodAvailability = {
  card: null,
  'pix-auto': null,
  'pix-once': null,
};

/**
 * Distinguishes "still pending" from "settled" subscription status. Used by
 * the PIX background poller to decide whether to keep polling or clear
 * pixPending. Settled = any non-pending state (success, failure, or trial).
 */
function isSettledStatus(s: SubscriptionData['status'] | undefined): boolean {
  if (!s) return false;
  return s !== 'PENDING' && s !== 'PENDING_AUTHORIZATION';
}

export function useSubscriptionState(
  client: HttpClient,
  appId: string,
  userId: string | null,
): SubscriptionState {
  const [sub, setSub] = useState<SubscriptionData | null>(() =>
    userId ? readCache(appId, userId) : null,
  );
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [pixPending, setPixPending] = useState<PixPending | null>(null);
  const [methodAvailability, setMethodAvailability] = useState<MethodAvailability>(
    INITIAL_METHOD_AVAILABILITY,
  );

  // Background PIX poller — single concurrent slot per state instance. New
  // checkout cancels prior polling. Cleanup on unmount or userId change.
  const pixPollAbortRef = useRef<{ cancelled: boolean } | null>(null);

  // userId troca (login, logout, troca de sessão) → re-hidrata do cache do
  // novo user e reseta initialLoadComplete. O refresh subsequente confirma
  // (ou corrige) o status.
  useEffect(() => {
    if (!userId) {
      setSub(null);
      setHasAccess(null);
      setPixPending(null);
      setMethodAvailability(INITIAL_METHOD_AVAILABILITY);
      setInitialLoadComplete(false);
      if (pixPollAbortRef.current) pixPollAbortRef.current.cancelled = true;
      return;
    }
    const cached = readCache(appId, userId);
    setSub(cached);
    setHasAccess(null);
    setInitialLoadComplete(false);
  }, [appId, userId]);

  // Cleanup polling on unmount.
  useEffect(() => {
    return () => {
      if (pixPollAbortRef.current) pixPollAbortRef.current.cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!userId) { setSub(null); setHasAccess(null); return; }
    setLoading(true);
    try {
      const res = await client.request<SubscriptionMeResponse>(
        'GET',
        `/api/app/${appId}/payments/subscription/me`,
      );
      setSub(res.subscription);
      setHasAccess(res.hasAccess);
      writeCache(appId, userId, res.subscription);
    } catch (err) {
      // Preserva last-good-state em erro transitório (429 do rate limit,
      // network blip, 5xx). Antes jogávamos `null` → mapStatus vira 'none'
      // → SubscriptionGate bloqueia → user com ACTIVE no DB via paywall
      // depois de Ctrl+Shift+R se 1º /me falhou (ex: post-checkout ainda
      // dentro da janela de rate limit do storm anterior).
      // Reset pra null só acontece quando userId muda (logout), handled acima.
      console.warn('sdk.subscription.refresh_failed', { err });
    } finally {
      setLoading(false);
      // Flipa 1× por (appId, userId). React descarta set idempotente,
      // então isso não re-memoiza o return em refreshes subsequentes.
      setInitialLoadComplete(true);
    }
  }, [client, appId, userId]);

  // W1B-3 (SDK 0.16.0): one-shot fresh fetch — bypasses cache. Caller awaits
  // backend's authoritative {subscription, hasAccess} response. Side-effect
  // free w.r.t. React state to avoid surprising consumers; if you want the
  // reactive snapshot to update, also call `refresh()`.
  const dataFetch = useCallback(async (): Promise<SubscriptionMeResponse> => {
    return client.request<SubscriptionMeResponse>(
      'GET',
      `/api/app/${appId}/payments/subscription/me`,
    );
  }, [client, appId]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Internal: poll backend for pix-once / pix-auto settlement. Stops on
  // settled status, max attempts, or external cancel. Updates `sub` +
  // `hasAccess` + clears `pixPending` when done.
  const startPixPolling = useCallback((method: 'pix-auto' | 'pix-once') => {
    if (pixPollAbortRef.current) pixPollAbortRef.current.cancelled = true;
    const token = { cancelled: false };
    pixPollAbortRef.current = token;
    void (async () => {
      for (let attempt = 0; attempt < PIX_POLL_MAX_ATTEMPTS; attempt++) {
        if (token.cancelled) return;
        await new Promise<void>((res) => setTimeout(res, PIX_POLL_INTERVAL_MS));
        if (token.cancelled) return;
        try {
          const res = await client.request<SubscriptionMeResponse>(
            'GET',
            `/api/app/${appId}/payments/subscription/me`,
          );
          if (token.cancelled) return;
          setSub(res.subscription);
          setHasAccess(res.hasAccess);
          if (userId) writeCache(appId, userId, res.subscription);
          if (res.subscription && isSettledStatus(res.subscription.status)) {
            setPixPending(null);
            return;
          }
        } catch (err) {
          // Transient — keep polling. Only console.warn on the last attempt
          // to avoid log spam on slow networks.
          if (attempt === PIX_POLL_MAX_ATTEMPTS - 1) {
            console.warn('sdk.subscription.pix_poll_failed', { err, method });
          }
        }
      }
      // Exhausted attempts — clear pixPending so UI can render expired state.
      if (!token.cancelled) setPixPending(null);
    })();
  }, [client, appId, userId]);

  // G154 fix (SDK 0.26.0). Consumer-side affordance to clear pixPending when
  // the user dismisses the PIX QR modal. Background polling continues — this
  // does NOT cancel the subscription. Stable reference (empty deps) — safe
  // for useEffect dep arrays in consumers.
  const clearPixPending = useCallback(() => {
    setPixPending(null);
  }, []);

  // Backend usa nomes legacy `creditCard` / `creditCardHolderInfo` no body
  // (espelha campos da API Asaas). SDK expõe os nomes friendlier `card` /
  // `holderInfo` na superfície pública e remapeia aqui antes do POST.
  const checkout = useCallback(
    async (args: CheckoutArgs): Promise<CheckoutResult> => {
      try {
        if (args.method === 'card') {
          const raw = await client.request<{
            subscriptionId: string;
            cardLast4: string | null;
            cardBrand: string | null;
            trialEndsAt: string | null;
            valueCents: number;
            cycle: CheckoutCycle;
          }>('POST', `/api/app/${appId}/payments/checkout/card`, {
            body: {
              cycle: args.cycle,
              cpf: args.cpf,
              creditCard: args.card,
              creditCardHolderInfo: args.holderInfo,
              ...(args.remoteIp ? { remoteIp: args.remoteIp } : {}),
            },
          });
          setMethodAvailability((m) => ({ ...m, card: true }));
          return {
            method: 'card',
            subscriptionId: raw.subscriptionId,
            cardLast4: raw.cardLast4,
            cardBrand: raw.cardBrand,
            trialEndsAt: raw.trialEndsAt,
            valueCents: raw.valueCents,
            cycle: raw.cycle,
          };
        }
        if (args.method === 'pix-auto') {
          const raw = await client.request<{
            subscriptionId: string;
            pixAuthorizationId: string;
            externalAuthorizationId: string;
            qrCodePayload: string | null;
            qrCodeBase64: string | null;
            valueCents: number;
            cycle: CheckoutCycle;
          }>('POST', `/api/app/${appId}/payments/checkout/pix`, {
            body: { cycle: args.cycle, cpf: args.cpf },
          });
          setMethodAvailability((m) => ({ ...m, 'pix-auto': true }));
          // W1B-6: backend returns flat keys; expose as PixPending reactive
          // state so UI can show QR while background polling watches for
          // settlement on /payments/subscription/me.
          if (raw.qrCodePayload) {
            setPixPending({
              qrCodePayload: raw.qrCodePayload,
              qrCodeBase64: raw.qrCodeBase64,
              method: 'pix-auto',
            });
            startPixPolling('pix-auto');
          }
          return {
            method: 'pix-auto',
            subscriptionId: raw.subscriptionId,
            pixAuthorizationId: raw.pixAuthorizationId,
            externalAuthorizationId: raw.externalAuthorizationId,
            qrCodePayload: raw.qrCodePayload,
            qrCodeBase64: raw.qrCodeBase64,
            valueCents: raw.valueCents,
            cycle: raw.cycle,
          };
        }
        // method === 'pix-once'
        const raw = await client.request<{
          subscriptionId: string;
          externalPaymentId: string;
          qrCodePayload: string | null;
          qrCodeBase64: string | null;
          expiresAt: string | null;
          valueCents: number;
          cycle: CheckoutCycle;
        }>('POST', `/api/app/${appId}/payments/checkout/pix-once`, {
          body: { cycle: args.cycle, cpf: args.cpf },
        });
        setMethodAvailability((m) => ({ ...m, 'pix-once': true }));
        if (raw.qrCodePayload) {
          setPixPending({
            qrCodePayload: raw.qrCodePayload,
            qrCodeBase64: raw.qrCodeBase64,
            method: 'pix-once',
          });
          startPixPolling('pix-once');
        }
        return {
          method: 'pix-once',
          subscriptionId: raw.subscriptionId,
          externalPaymentId: raw.externalPaymentId,
          qrCodePayload: raw.qrCodePayload,
          qrCodeBase64: raw.qrCodeBase64,
          expiresAt: raw.expiresAt,
          valueCents: raw.valueCents,
          cycle: raw.cycle,
        };
      } catch (err) {
        // W1B-7: surface per-app feature gates as a discriminated result
        // instead of throwing. Today's known gate is pix-once
        // (`payments.checkout.pix_once_disabled` 403). Future gates extend
        // this map; unknown SdkErrors still throw.
        if (err instanceof SdkError && err.code === 'payments.checkout.pix_once_disabled') {
          setMethodAvailability((m) => ({ ...m, 'pix-once': false }));
          return {
            ok: false,
            code: 'method_unavailable',
            method: 'pix-once',
            reason: err.code,
          };
        }
        throw err;
      }
    },
    [client, appId, startPixPolling],
  );

  // Legacy wrappers: pre-0.16.0 callers don't know about MethodUnavailableResult.
  // Coerce the new failure variant back to a thrown SdkError so the deprecated
  // surface keeps its old contract (throws on 403). New code should call
  // `subscription.checkout(args)` directly and handle the discriminator.
  const checkoutCard = useCallback(
    async (args: CheckoutCardArgs): Promise<CardCheckoutResult> => {
      const result = await checkout({
        method: 'card',
        cycle: args.cycle ?? 'MONTHLY',
        cpf: args.cpf,
        card: args.card,
        holderInfo: args.holderInfo,
        ...(args.remoteIp ? { remoteIp: args.remoteIp } : {}),
      });
      if ('ok' in result && result.ok === false) {
        throw new SdkError(`checkout failed: ${result.code}`, result.reason, 403);
      }
      return result as CardCheckoutResult;
    },
    [checkout],
  );

  const checkoutPixAuto = useCallback(
    async (args: CheckoutPixArgs): Promise<PixAutoCheckoutResult> => {
      const result = await checkout({
        method: 'pix-auto',
        cycle: args.cycle ?? 'MONTHLY',
        cpf: args.cpf,
      });
      if ('ok' in result && result.ok === false) {
        throw new SdkError(`checkout failed: ${result.code}`, result.reason, 403);
      }
      return result as PixAutoCheckoutResult;
    },
    [checkout],
  );

  const checkoutPixOnce = useCallback(
    async (args: CheckoutPixArgs): Promise<PixOnceCheckoutResult> => {
      const result = await checkout({
        method: 'pix-once',
        cycle: args.cycle ?? 'MONTHLY',
        cpf: args.cpf,
      });
      if ('ok' in result && result.ok === false) {
        throw new SdkError(`checkout failed: ${result.code}`, result.reason, 403);
      }
      return result as PixOnceCheckoutResult;
    },
    [checkout],
  );

  const cancel = useCallback(async (): Promise<CancelResult> => {
    const raw = await client.request<{
      subscriptionId: string;
      subscriptionStatus: 'CANCELLED';
      refundedPaymentId?: string;
      refundedValueCents?: number;
    }>('DELETE', `/api/app/${appId}/payments/subscription`);
    return {
      subscriptionId: raw.subscriptionId,
      subscriptionStatus: 'canceled',
      ...(raw.refundedPaymentId ? { refundedPaymentId: raw.refundedPaymentId } : {}),
      ...(raw.refundedValueCents != null ? { refundedValueCents: raw.refundedValueCents } : {}),
    };
  }, [client, appId]);

  // Memoiza o objeto retornado pra ele ser referencialmente estável entre
  // renders quando deps reais não mudaram. Sem isso, todo useEffect upstream
  // que depende de `subscription` re-trigga a cada render → loop infinito
  // de polling (descoberto durante POC Pulse: 12k req/min do mesmo user).
  //
  // CUIDADO (2026-04-21): `loading` NÃO entra nas deps. Cada refresh flipa
  // loading 2× (true→false) — se estivesse nas deps, re-memoiza 2× por
  // refresh, effect `[subscription]` em PaymentReturnHandler re-fire 2× sem
  // esperar setTimeout → tick imediato em cada flip → storm exponencial
  // (258→654 req em 20s, bug observado em QA papomaterno). O campo `loading`
  // é mantido na API pública mas captura snapshot do momento em que outra
  // dep muda (sub/refresh). Nenhum consumer hoje lê `subscription.loading`
  // reativo; se algum adicionar, precisa de hook separado (`useRefreshing()`)
  // ou ler via ref. Deps reais: sub (state) + refresh/checkoutCard/cancel
  // (useCallback stables).
  return useMemo(
    () => ({
      status: () => mapStatus(sub),
      daysLeftInTrial: () => daysLeft(sub),
      current: sub,
      hasAccess,
      methodAvailability,
      pixPending,
      clearPixPending,
      data: dataFetch,
      refresh,
      loading,
      initialLoadComplete,
      checkout,
      checkoutCard,
      checkoutPixAuto,
      checkoutPixOnce,
      cancel,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sub, hasAccess, methodAvailability, pixPending, clearPixPending, initialLoadComplete, refresh, dataFetch, checkout, checkoutCard, checkoutPixAuto, checkoutPixOnce, cancel],
  );
}
